import { FV, PMT, PV } from "@/lib/excel";
import { migrate } from "@/lib/migrate";
import { ASSET_ROWS } from "@/lib/schema";

const n = (v) => Number(v) || 0;
const p = (v) => n(v) / 100;
const filled = (v) => v !== "" && v !== undefined && v !== null;
const sumAmt = (list) => list.reduce((a, x) => a + n(x?.amount), 0);
export const CATEGORIES = ["Housing", "Food", "Transportation", "Healthcare", "Lifestyle", "Other", "Banking", "Taxes"];

export const ageFrom = (dob, asOf) => {
  if (!dob) return null;
  const b = new Date(dob);
  const t = asOf ? new Date(asOf) : new Date();
  if (Number.isNaN(b.getTime())) return null;
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a -= 1;
  return a;
};
export const personAge = (person, asOf) => ageFrom(person?.dob, asOf) ?? n(person?.age);

// Excel sheet "Expenses"
export function calcExpenses(age, inflation, list = []) {
  const infl = p(inflation);
  const items = list.map((it) => {
    const isTax = it.category === "Taxes";
    const formula = it.need === "Y" && !isTax ? FV(infl, 65 - age, 0, -n(it.current)) : 0;
    return { ...it, isTax, formulaRet: formula, ret: filled(it.retOverride) ? n(it.retOverride) : formula };
  });
  const taxes = items.filter((i) => i.isTax);
  const taxesAnnual = taxes.reduce((a, i) => a + n(i.current), 0);
  const taxesRet = taxes.reduce((a, i) => a + i.ret, 0);
  const totalMonthly = items.reduce((a, i) => a + n(i.current), 0) - taxesAnnual;
  const retMonthly = items.reduce((a, i) => a + i.ret, 0);
  const byCategory = CATEGORIES.filter((c) => c !== "Taxes").map((c) => {
    const cur = items.filter((i) => i.category === c).reduce((a, i) => a + n(i.current), 0);
    const ret = items.filter((i) => i.category === c).reduce((a, i) => a + i.ret, 0);
    return { category: c, cur, curYear: cur * 12, share: totalMonthly ? cur / totalMonthly : 0, ret, retYear: ret * 12 };
  });
  const rent = items.filter((i) => i.id === "rent").reduce((a, i) => a + n(i.current), 0);
  return { items, byCategory, taxesAnnual, taxesRet, rent, totalMonthly, totalYearly: totalMonthly * 12 + taxesAnnual, retMonthly, retYearly: retMonthly * 12 };
}

function calcOverview(d, exp) {
  const inc = d.financial.income;
  const A = d.financial.assets;
  const L = d.financial.liabilities;
  const accounts = d.investments.accounts;
  const annualIncome = n(inc.clientAnnual) + n(inc.spouseAnnual) + n(inc.otherAnnual);
  const monthlyIncome = annualIncome / 12;
  const takeHomeMonthly = n(inc.clientTakeHome) + n(inc.spouseTakeHome) + n(inc.otherTakeHome);
  const debtPayments = Object.entries(L).filter(([k]) => k !== "mortgage").reduce((a, [, x]) => a + n(x.payment), 0);
  const taxesMonthly = exp.taxesAnnual / 12;
  const otherAssets = sumAmt(ASSET_ROWS.map(([k]) => A[k]));
  const investmentsTotal = accounts.reduce((a, x) => a + n(x.value), 0);
  const totalAssets = otherAssets + investmentsTotal;
  const totalLiabilities = sumAmt(Object.values(L));
  const availableAtDeath = sumAmt(ASSET_ROWS.map(([k]) => A[k]).filter((x) => x?.use === "Y"))
    + accounts.filter((x) => x.use === "Y").reduce((a, x) => a + n(x.value), 0);
  return {
    annualIncome, monthlyIncome, takeHomeMonthly, debtPayments, taxesMonthly, otherAssets, investmentsTotal, totalAssets, totalLiabilities, availableAtDeath,
    mortgage: n(L.mortgage?.amount), cashFlow: takeHomeMonthly > 0 ? takeHomeMonthly - exp.totalMonthly - debtPayments : null, netWorth: totalAssets - totalLiabilities,
  };
}

// Life / DI / CI per person. Existing coverage and assets are each counted once.
function calcPerson(pi, income, exp, ov, d) {
  const years = n(pi.yearsNeeded);
  const incomeRepl = pi.incomeMethod === "Y" ? p(pi.incomePct) * years * income : null;
  const familyNeeds = pi.expenseMethod === "Y" ? (exp.totalMonthly - exp.rent) * 12 * years : null;
  const liabilities = sumAmt(Object.values(d.financial.liabilities).filter((x) => x.use === "Y"));
  const obligations = sumAmt(Object.values(d.insurance.finals).filter((x) => x.use === "Y"));
  const totalNeed = n(incomeRepl) + n(familyNeeds) + liabilities + obligations;
  const existingLife = n(pi.permanentLife) + n(pi.termLife) + n(pi.groupLife);
  const lifeAdditional = Math.max(0, totalNeed - existingLife - ov.availableAtDeath);
  const diRequired = (income / 12) * p(pi.diPct);
  const diExisting = n(pi.groupLtd) + n(pi.individualDi);
  const ciRequired = income * n(pi.ciYears);
  return {
    income, years, incomeRepl, familyNeeds, liabilities, obligations, totalNeed, existingLife, availableAssets: ov.availableAtDeath, lifeAdditional,
    diRequired, diExisting, diShortfall: Math.max(0, diRequired - diExisting),
    ciRequired, ciExisting: n(pi.criticalIllness), ciAdditional: Math.max(0, ciRequired - n(pi.criticalIllness)),
  };
}

// Excel sheet "Investment Need Analysis", generalised to an account list and an optional spouse
const accountLabel = (a) => `${a.type}${a.owner && a.owner !== "Client" ? ` (${a.owner})` : ""}`;
const num = (v) => (typeof v === "number" ? v : 0);

function projectPerson({ age, retAge, lifeExp, h4, R, accounts, tag }) {
  const yrs = retAge - age;
  const drawMonths = (lifeExp - retAge) * 12;
  const list = accounts.map((a) => {
    const annual = n(a.monthly) * 12;
    const ror = p(a.ror);
    const pension = a.type === "Pension";
    const fv = pension ? FV(ror / 26, yrs * 26, -(annual / 26), -n(a.value), 0) : FV(ror, yrs, -annual, -n(a.value), 1);
    const draw = pension ? PMT(0.02 / 12, drawMonths, -fv) : PMT(h4, a.type === "Non-registered" ? 240 : drawMonths, -fv);
    return { ...a, label: accountLabel(a), included: true, fv, draw };
  });
  const cpp = retAge < 65 ? "CPP Starts from 65" : n(R.cppMonthly);
  const oasFormula = retAge < 65 ? "OAS starts at age 65" : -FV(0.02, 65 - age, 0, 742.31, 1) * (n(R.yearsInCanada) / 40);
  const oas = filled(R.oasOverride) ? n(R.oasOverride) : oasFormula;
  const employerPension = n(R.employerPensionMonthly);
  const otherMonthly = n(R.otherMonthlyIncome);
  const drawTotal = list.reduce((t, x) => t + x.draw, 0);
  const sfx = tag ? ` (${tag})` : "";
  const sources = [[`CPP${sfx}`, num(cpp)], [`OAS${sfx}`, num(oas)], [`Employer pension${sfx}`, employerPension], ...list.map((x) => [x.label, x.draw]), [`Other income${sfx}`, otherMonthly]];
  return {
    age, retAge, lifeExp, yrs, cpp, oas, oasFormula, employerPension, otherMonthly, accounts: list, drawTotal,
    expected: num(cpp) + num(oas) + employerPension + otherMonthly + drawTotal,
    totalProjected: list.reduce((t, x) => t + x.fv, 0), existingSavings: list.reduce((t, x) => t + n(x.value), 0),
    sources: sources.filter(([, v]) => v > 0),
  };
}

function calcRetirement(d, age, spouseAge, exp) {
  const R = d.retirement;
  const post = p(R.postRetRor);
  const infl = p(d.assumptions.inflation);
  const h4 = (1 + (Math.pow(1 + post, 1 / 12) - 1)) / (1 + (Math.pow(1 + infl, 1 / 12) - 1)) - 1;
  const incl = d.investments.accounts.filter((a) => a.retirement === "Y");
  const excluded = d.investments.accounts.filter((a) => a.retirement !== "Y").map((a) => ({ ...a, label: accountLabel(a), included: false }));
  const withSpouse = R.includeSpouse === "Y";
  const spouseActive = withSpouse && spouseAge > 0;
  const isSp = (a) => spouseActive && a.owner === "Spouse";
  const client = projectPerson({ age, retAge: n(d.profile.retirementAge), lifeExp: n(d.profile.lifeExpectancy), h4, R, accounts: incl.filter((a) => !isSp(a)) });
  const spouse = spouseActive
    ? projectPerson({ age: spouseAge, retAge: n(d.profile.spouse.retirementAge), lifeExp: n(d.profile.spouse.lifeExpectancy), h4, R: R.spouse || {}, accounts: incl.filter(isSp), tag: "Spouse" })
    : null;
  const { retAge, lifeExp } = client;
  const autoGoal = exp.retMonthly;
  const monthlyGoal = filled(R.desiredMonthlyOverride) ? n(R.desiredMonthlyOverride) : autoGoal;
  const annualGoal = monthlyGoal * 12;
  const expectedMonthly = client.expected + (spouse?.expected || 0);
  const annualSurplus = expectedMonthly * 12 - annualGoal;
  const monthlySurplus = annualSurplus / 12;
  const fin = PV((1 + post) / (1 + infl) - 1, lifeExp - retAge, -annualGoal, 0, 1);
  const savingsNeeded = monthlySurplus < 0 ? PV(Math.pow((1 + post) / (1 + infl), 1 / 12) - 1, 240, monthlySurplus, 0, 1) : null;
  const annualContribution = savingsNeeded !== null ? PMT(p(R.targetRor), 65 - age, 0, -savingsNeeded, 1) : null;
  return {
    age, retAge, lifeExp, yrs: client.yrs, cpp: client.cpp, oas: client.oas, oasFormula: client.oasFormula,
    employerPension: client.employerPension, otherMonthly: client.otherMonthly,
    client, spouse, withSpouse, accounts: [...client.accounts, ...(spouse?.accounts || [])], excluded,
    autoGoal, monthlyGoal, annualGoal, expectedMonthly, annualSurplus, monthlySurplus, fin, savingsNeeded, annualContribution,
    monthlyContribution: annualContribution !== null ? annualContribution / 12 : null,
    existingSavings: client.existingSavings + (spouse?.existingSavings || 0),
    excludedValue: excluded.reduce((t, x) => t + n(x.value), 0),
    totalProjected: client.totalProjected + (spouse?.totalProjected || 0),
    sources: [...client.sources, ...(spouse?.sources || [])],
  };
}

export const NEEDS = [["life", "Life insurance", "lifeAdditional"], ["di", "Disability insurance", "diShortfall"], ["ci", "Critical illness", "ciAdditional"]];

function calcPlan(d, ins, ret) {
  const rows = [];
  const people = [["client", d.profile.name || "Client"], ...(ins.hasSpouse ? [["spouse", d.profile.spouse.name || "Spouse"]] : [])];
  people.forEach(([k, name]) => NEEDS.forEach(([key, label, gapKey]) => {
    const r = d.recommendations?.[k]?.[key] || {};
    if (r.product || filled(r.premium)) {
      rows.push({ id: `${k}-${key}`, need: label, person: name, gap: ins[k][gapKey], monthlyGap: key === "di", product: r.product || "", premium: n(r.premium) });
    }
  }));
  const r = d.recommendations?.retirement || {};
  if (r.product || filled(r.premium)) {
    rows.push({ id: "retirement", need: "Retirement savings", person: ret.withSpouse ? "Household" : d.profile.name || "Client", gap: ret.monthlyContribution ?? 0, monthlyGap: true, product: r.product || "", premium: n(r.premium) });
  }
  return { rows, totalPremium: rows.reduce((t, x) => t + x.premium, 0) };
}

export function computeSynopsis(raw) {
  const d = migrate(raw);
  const asOf = d.profile.meetingDate;
  const age = personAge(d.profile, asOf);
  const spouseAge = personAge(d.profile.spouse, asOf);
  const exp = calcExpenses(age, d.assumptions.inflation, d.financial.expenses.items);
  const ov = calcOverview(d, exp);
  const inc = d.financial.income;
  const hasSpouse = !!d.profile.spouse.name || ["Married", "Common-law"].includes(d.profile.maritalStatus);
  const ins = {
    hasSpouse,
    client: calcPerson(d.insurance.client, n(inc.clientAnnual), exp, ov, d),
    spouse: calcPerson(d.insurance.spouse, n(inc.spouseAnnual), exp, ov, d),
  };
  const ret = calcRetirement(d, age, spouseAge, exp);
  return { d, age, spouseAge, exp, ov, ins, ret, plan: calcPlan(d, ins, ret) };
}

export function recommendedActions(s, cur, money) {
  const out = [];
  const recTxt = (r) => (r && (r.product || filled(r.premium)) ? ` Recommended: ${r.product || "plan"}${filled(r.premium) ? ` (${money(n(r.premium), cur)}/mo)` : ""}.` : "");
  const R = s.d.recommendations || {};
  const people = [["client", s.d.profile.name || "Client"], ...(s.ins.hasSpouse ? [["spouse", s.d.profile.spouse.name || "Spouse"]] : [])];
  people.forEach(([k, name]) => {
    const r = s.ins[k];
    const rc = R[k] || {};
    if (r.lifeAdditional > 0) out.push(`Additional life insurance of ${money(r.lifeAdditional, cur)} for ${name}.${recTxt(rc.life)}`);
    if (r.diShortfall > 0) out.push(`Disability coverage of ${money(r.diShortfall, cur)}/month for ${name} to protect income.${recTxt(rc.di)}`);
    if (r.ciAdditional > 0) out.push(`Critical illness coverage of ${money(r.ciAdditional, cur)} for ${name}.${recTxt(rc.ci)}`);
  });
  if (s.ret.monthlyContribution) out.push(`Save an additional ${money(s.ret.monthlyContribution, cur)}/month toward retirement to close the ${money(Math.abs(s.ret.monthlySurplus), cur)}/month income gap.${recTxt(R.retirement)}`);
  if (s.ov.cashFlow !== null && s.ov.cashFlow < 0) out.push(`Monthly spending exceeds income by ${money(Math.abs(s.ov.cashFlow), cur)}; review the budget.`);
  if (!out.length) out.push("Your current plan meets the calculated needs. Review annually or after major life events.");
  return out;
}
